<template>
  <div class="v2-flex v2-gap-2">
    <v2-button
      variant="outline"
      @click="$emit('download-shipment')"
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line
          x1="12"
          y1="15"
          x2="12"
          y2="3"
        />
      </svg>
      Download Shipment
    </v2-button>

    <v2-dropdown
      v-if="isBrand"
      :options="[]"
      align="right"
    >
      <template #toggle="{ isOpen }">
        <v2-button
          variant="primary"
          class="v2-shipment-action__invoice-toggle"
        >
          Invoices
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="v2-shipment-action__toggle-chevron"
            :class="{ 'v2-shipment-action__toggle-chevron--open': isOpen }"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </v2-button>
      </template>
      <template #menu="{ close }">
        <div class="v2-shipment-action__invoice-options">
          <button
            v-if="invoiceUrl"
            class="v2-shipment-action__invoice-option"
            @click="download(invoiceUrl); close()"
          >
            Download {{ getFileName(FILE_TYPES.INVOICE_BY_BRAND, invoiceUrl) }}
          </button>
          <button
            class="v2-shipment-action__invoice-option"
            @click="onClickUploadInvoice(); close()"
          >
            {{ isUpdateInvoiceEnabled ? 'Update' : 'Upload' }} Invoice
          </button>
        </div>
      </template>
    </v2-dropdown>

    <!-- Invoice Upload Modal -->
    <v2-modal
      :show="showInvoiceModal"
      title="Upload invoice"
      size="lg"
      @close="closeModal"
    >
      <div
        class="v2-shipment-action__form"
        @dragover.prevent
        @drop.prevent
      >
        <!-- File upload area -->
        <div>
          <label class="v2-shipment-action__label">
            Upload Invoice
            <span class="v2-shipment-action__required">*</span>
          </label>
          <div v-if="!invoiceFile">
            <div
              class="v2-shipment-action__dropzone"
              @drop="onDropInvoiceFile"
              @click="$refs.fileInput.click()"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line
                  x1="12"
                  y1="3"
                  x2="12"
                  y2="15"
                />
              </svg>
              <p class="v2-shipment-action__dropzone-title">
                Drag & Drop
              </p>
              <p class="v2-shipment-action__dropzone-subtitle">
                or click to select files from your system
              </p>
            </div>
            <input
              ref="fileInput"
              type="file"
              class="v2-shipment-action__hidden-input"
              @change="onFileSelected"
            >
          </div>
          <div
            v-else
            class="v2-shipment-action__file-info"
            @drop="onDropInvoiceFile"
          >
            <div class="v2-shipment-action__file-details">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
              <div>
                <div class="v2-shipment-action__file-name">
                  {{ invoiceFile.name }}
                </div>
                <div
                  v-if="invoiceFile.size"
                  class="v2-shipment-action__file-size"
                >
                  {{ formatFileSize(invoiceFile.size) }}
                </div>
              </div>
            </div>
            <button
              class="v2-shipment-action__remove-btn"
              @click="invoiceFile = null"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            </button>
          </div>
        </div>

        <!-- Invoice amount + currency -->
        <div class="v2-flex v2-gap-3">
          <div class="v2-shipment-action__field v2-shipment-action__field--grow">
            <v2-input
              v-model="invoiceAmount"
              label="Invoice Amount"
              type="number"
              placeholder="Type in the invoice amount"
            />
          </div>
          <div class="v2-shipment-action__field v2-shipment-action__field--sm">
            <label class="v2-shipment-action__label">
              Currency
              <span class="v2-shipment-action__required">*</span>
            </label>
            <v2-dropdown
              :options="currencyOptions"
              :value="currency"
              placeholder="Select"
              @select="onSelectCurrency"
            />
          </div>
        </div>

        <!-- Invoice quantity -->
        <div class="v2-shipment-action__field v2-shipment-action__field--half">
          <v2-input
            v-model="invoiceQuantity"
            label="Invoice Quantity"
            type="number"
            placeholder="Type in the invoice quantity"
          />
        </div>

        <!-- Invoice amount in USD -->
        <div class="v2-shipment-action__field">
          <v2-input
            :value="getInvoiceAmountInUSDwithSymbol"
            label="Invoice Amount in USD"
            disabled
            placeholder="Invoice amount in USD"
          />
        </div>

        <!-- Expected Amount collapse -->
        <div>
          <button
            class="v2-shipment-action__collapse-toggle"
            @click="isVisible = !isVisible"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              :class="{ 'v2-shipment-action__chevron--open': isVisible }"
              class="v2-shipment-action__chevron"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
            <span class="v2-font-bold">Expected Amount:</span>
            <span class="v2-shipment-action__expected-value">
              {{ formatNumber(shipment.totalPriceWithVatForBrand || shipment.totalPriceForBrand, 2) }} USD
            </span>
          </button>
          <div
            v-if="isVisible"
            class="v2-shipment-action__collapse-content"
          >
            <div class="v2-shipment-action__collapse-row">
              <span>Total quantity:</span>
              <span class="v2-text-muted">{{ shipment.totalQuantity }}</span>
            </div>
            <div class="v2-shipment-action__collapse-row">
              <span>Total cost:</span>
              <span class="v2-text-muted">{{ formatNumber(shipment.totalPriceForBrand, 2) }} USD</span>
            </div>
            <div class="v2-shipment-action__collapse-row">
              <span>Amount with VAT:</span>
              <span class="v2-text-muted">{{ formatNumber(shipment.totalPriceWithVatForBrand || shipment.totalPriceForBrand, 2) }} USD</span>
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <v2-button
          variant="outline"
          @click="closeModal"
        >
          Cancel
        </v2-button>
        <v2-button
          variant="primary"
          :disabled="isUploading || !isValid"
          @click="handleUploadInvoice"
        >
          {{ isDispute ? (isUploading ? 'Disputing' : 'Dispute') : (isUploading ? 'Uploading' : 'Upload') }}
        </v2-button>
      </template>
    </v2-modal>
  </div>
</template>

<script>
import { toRefs } from '@vue/composition-api'
import V2Button from '@/components/v2/V2Button.vue'
import V2Modal from '@/components/v2/V2Modal.vue'
import V2Input from '@/components/v2/V2Input.vue'
import V2Dropdown from '@/components/v2/V2Dropdown.vue'
import { constants as c } from '@kingpin-global/kingpin-utils-frontend'
import { useShipmentAction } from '@/composables/useShipmentAction'

const { CURRENCY_CONVERSIONS } = c

export default {
  name: 'V2ShipmentAction',
  components: {
    V2Button,
    V2Modal,
    V2Input,
    V2Dropdown,
  },
  props: {
    shipment: {
      type: Object,
      default() {
        return {}
      },
    },
  },
  emits: ['load-shipment'],
  data() {
    return {
      showInvoiceModal: false,
    }
  },
  computed: {
    currencyOptions() {
      return Object.values(CURRENCY_CONVERSIONS).map(curr => curr.LABEL)
    },
  },
  setup(props) {
    const { shipment } = toRefs(props)
    const shipmentAction = useShipmentAction(shipment)
    shipmentAction.initInvoiceUrl()
    return {
      ...shipmentAction,
    }
  },
  methods: {
    onClickUploadInvoice() {
      this.showInvoiceModal = true
      if (this.isUpdateInvoiceEnabled) {
        this.setEditableFields()
      }
    },
    async handleUploadInvoice() {
      const result = await this.uploadInvoice()
      if (result?.success) {
        this.showInvoiceModal = false
        this.$emit('load-shipment')
      }
    },
    closeModal() {
      this.clearForm()
      this.showInvoiceModal = false
    },
    onFileSelected(e) {
      const file = e.target.files[0]
      if (file) {
        this.invoiceFile = file
      }
    },
    onSelectCurrency(value) {
      this.currency = value
    },
    formatFileSize(bytes) {
      return `${(bytes / 1024).toFixed(2)} KB`
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-shipment-action {
  &__form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  &__label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--v2-foreground);
    margin-bottom: 0.5rem;
  }

  &__required {
    color: var(--v2-destructive);
  }

  &__dropzone {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    border: 2px dashed var(--v2-border);
    border-radius: 0.625rem;
    cursor: pointer;
    color: var(--v2-muted-foreground);
    transition: border-color 0.15s ease;

    &:hover {
      border-color: var(--v2-primary);
    }
  }

  &__dropzone-title {
    font-size: 0.9375rem;
    font-weight: 700;
    color: var(--v2-foreground);
    margin: 0.5rem 0 0.25rem;
  }

  &__dropzone-subtitle {
    font-size: 0.8125rem;
    color: var(--v2-muted-foreground);
    margin: 0;
  }

  &__hidden-input {
    display: none;
  }

  &__file-info {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1rem;
    // Glass card — selected-file row inside the shipment modal.
    border: 1px solid var(--v2-glass-stroke);
    border-radius: 0.625rem;
    background: var(--v2-glass-surface-2);
    backdrop-filter: blur(var(--v2-glass-blur-sm)) saturate(160%);
  }

  &__file-details {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: var(--v2-muted-foreground);
  }

  &__file-name {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--v2-foreground);
  }

  &__file-size {
    font-size: 0.75rem;
    color: var(--v2-muted-foreground);
  }

  &__remove-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.25rem;
    background: none;
    border: none;
    color: var(--v2-destructive);
    cursor: pointer;
    border-radius: 0.25rem;

    &:hover {
      background: rgba(229, 72, 77, 0.1);
    }
  }

  &__field {
    &--grow {
      flex: 1;
    }

    &--sm {
      width: 120px;
      flex-shrink: 0;
    }

    &--half {
      width: 50%;
    }
  }

  &__collapse-toggle {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    background: none;
    border: none;
    cursor: pointer;
    font-size: 0.875rem;
    color: var(--v2-foreground);
    padding: 0;
  }

  &__expected-value {
    color: var(--v2-primary);
  }

  &__chevron {
    transition: transform 0.15s ease;

    &--open {
      transform: rotate(90deg);
    }
  }

  &__collapse-content {
    margin-top: 0.5rem;
    padding: 0.75rem;
    // Glass card — collapsible detail panel inside the action modal.
    background: var(--v2-glass-surface-2);
    border: 1px solid var(--v2-glass-stroke);
    border-radius: 0.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    font-size: 0.875rem;
  }

  &__collapse-row {
    display: flex;
    justify-content: space-between;
  }
}

.v2-text-muted {
  color: var(--v2-muted-foreground);
}

.v2-shipment-action__invoice-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
}

.v2-shipment-action__toggle-chevron {
  transition: transform 0.15s ease;

  &--open {
    transform: rotate(180deg);
  }
}

.v2-shipment-action__invoice-options {
  padding: 0.25rem 0;
}

.v2-shipment-action__invoice-option {
  display: block;
  width: 100%;
  padding: 0.5rem 0.75rem;
  font-family: var(--v2-font-sans);
  font-size: 0.875rem;
  color: var(--v2-foreground);
  background: transparent;
  border: none;
  text-align: left;
  cursor: pointer;
  transition: background 0.1s ease;
  white-space: nowrap;

  &:hover {
    background: var(--v2-glass-surface-2);
  }
}
</style>
